import { ReactNode, useState, useEffect, createContext, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { logout } from '../store/slices/authSlice';
import { setMobileTab } from '../store/slices/uiSlice';
import { sendMdtDispatch, respondToDispatch, cancelDispatch } from '../store/slices/dispatchSlice';
import type { MdtDispatch } from '../types';
import { Home, MessageSquare, Map, LogOut, FileText } from 'lucide-react';
import { cn, API_BASE_URL } from '../lib/utils';
import MdtDispatchAlert from '../components/mobile/MdtDispatchAlert';
import type { Resource } from '../types';
import { usePushSubscription } from '../hooks/usePushSubscription';

type UnitStatus = 'available' | 'busy' | 'enroute' | 'onscene' | 'onbreak' | 'conveying';

interface MobileContextType {
  unitStatus: UnitStatus;
  setUnitStatus: (s: UnitStatus) => void;
  chatOpen: boolean;
  setChatOpen: (v: boolean) => void;
  mdtResource: Resource | null;
}

const MobileContext = createContext<MobileContextType>({
  unitStatus: 'available',
  setUnitStatus: () => {},
  chatOpen: false,
  setChatOpen: () => {},
  mdtResource: null,
});

export const useMobileContext = () => useContext(MobileContext);

const statusGlow: Record<UnitStatus, string> = {
  available: '#44edaa',
  busy: '#ffb454',
  enroute: '#3de8e0',
  conveying: '#fb923c',
  onscene: '#ff5074',
  onbreak: '#b88cff',
};

const tabs = [
  { id: 'dispatch' as const, icon: Home, label: 'Home', path: '/' },
  { id: 'chat' as const, icon: MessageSquare, label: 'Chat', path: '/chat' },
  { id: 'map' as const, icon: Map, label: 'Map', path: '/map' },
  { id: 'incident' as const, icon: FileText, label: 'Incident', path: '/incident' },
];

interface MobileLayoutProps {
  children: ReactNode;
  mdtResource?: Resource | null;
}

export default function MobileLayout({ children, mdtResource = null }: MobileLayoutProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAppSelector((state) => state.auth);
  useAppSelector((state) => state.ui);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [unitStatus, setUnitStatus] = useState<UnitStatus>('available');
  const [chatOpen, setChatOpen] = useState(false);

  // Setup push notifications
  usePushSubscription();

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Request notification permission on mount so we can alert the user when
  // dispatches/emergencies arrive while the screen/tab is in the background.
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);

  // Screen Wake Lock — keep the MDT screen awake while the app is open
  // (prevents auto-sleep; user can still manually lock the device).
  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null;
    let cancelled = false;

    const requestLock = async () => {
      try {
        const nav = navigator as Navigator & { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> } };
        if (nav.wakeLock?.request) {
          wakeLock = await nav.wakeLock.request('screen');
        }
      } catch { /* ignore — not supported */ }
    };

    // Re-acquire on visibility changes (lock is released when page hidden)
    const onVisibility = () => {
      if (!cancelled && document.visibilityState === 'visible') {
        requestLock();
      }
    };

    requestLock();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      try { wakeLock?.release(); } catch { /* ignore */ }
    };
  }, []);

  // GPS tracking: send location via HTTP POST every 10 seconds
  useEffect(() => {
    if (!mdtResource || !user || !token) return;

    const postPing = (latitude: number, longitude: number) => {
      fetch(`${API_BASE_URL}/tracking/ping`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          resourceId: mdtResource.id,
          resourceName: mdtResource.name,
          userId: user.id,
          userName: `${user.firstName} ${user.lastName}`,
          latitude,
          longitude,
          status: unitStatus,
        }),
      }).catch(() => {});
    };

    const sendLocation = () => {
      if (!navigator.geolocation) {
        postPing(40.7128 + (Math.random() - 0.5) * 0.02, -74.006 + (Math.random() - 0.5) * 0.02);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => postPing(pos.coords.latitude, pos.coords.longitude),
        () => postPing(40.7128 + (Math.random() - 0.5) * 0.02, -74.006 + (Math.random() - 0.5) * 0.02),
        { enableHighAccuracy: true, timeout: 4000 }
      );
    };

    sendLocation();
    const interval = setInterval(sendLocation, 10000); // 10-second ping interval

    return () => {
      clearInterval(interval);
      // Notify backend that this MDT has disconnected
      fetch(`${API_BASE_URL}/tracking/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ resourceId: mdtResource.id }),
      }).catch(() => {});
    };
  }, [mdtResource, user, unitStatus, token]);

  // Poll for incoming dispatches and dispatch responses every 3 seconds
  useEffect(() => {
    if (!mdtResource || !token) return;
    const seenIds = new Set<string>();

    const poll = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/dispatches?targetUnitId=${encodeURIComponent(mdtResource.id)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const data = await res.json();
        const list: MdtDispatch[] = data.dispatches || [];

        list.forEach((d) => {
          if (d.status === 'pending' && !seenIds.has(d.id)) {
            seenIds.add(d.id);
            dispatch(sendMdtDispatch(d));

            // Vibrate the device with an urgent pattern
            try {
              if ('vibrate' in navigator) {
                navigator.vibrate([300, 100, 300, 100, 600]);
              }
            } catch { /* ignore */ }

            // System notification (visible on lock screen for installed PWAs on Android)
            try {
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                const n = new Notification(`Incoming Dispatch — ${d.incidentSeverity?.toUpperCase() || ''}`, {
                  body: `${d.incidentTitle}\n${d.incidentLocation?.address || ''}`,
                  tag: `dispatch-${d.id}`,
                  requireInteraction: true,
                  icon: '/icons/icon-192x192.png',
                  badge: '/icons/icon-192x192.png',
                });
                n.onclick = () => { window.focus(); n.close(); };
              }
            } catch { /* ignore */ }
          } else if (d.status === 'cancelled' && !seenIds.has(`cancel-${d.id}`)) {
            seenIds.add(`cancel-${d.id}`);
            dispatch(cancelDispatch(d.id));
          } else if ((d.status === 'accepted' || d.status === 'rejected') && d.respondedAt && !seenIds.has(`resp-${d.id}`)) {
            seenIds.add(`resp-${d.id}`);
            dispatch(respondToDispatch({ id: d.id, status: d.status, respondedAt: d.respondedAt }));
          }
        });
      } catch { /* ignore */ }
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [mdtResource, token, dispatch]);

  const handleTabChange = (tab: typeof tabs[number]) => {
    if (tab.id === 'chat') {
      setChatOpen(true);
      return;
    }
    setChatOpen(false);
    dispatch(setMobileTab(tab.id as 'dispatch' | 'incidents' | 'map' | 'profile'));
    navigate(tab.path);
  };

  return (
    <MobileContext.Provider value={{ unitStatus, setUnitStatus, chatOpen, setChatOpen, mdtResource }}>
      <div className="mobile-dispatch-layout">
        {/* Ambient status-reactive background */}
        <div className={cn('ambient-bg', `status-${unitStatus}`)} />
        <div className="ambient-glow" style={{ background: statusGlow[unitStatus] }} />

        {/* Glass Header */}
        <header className="relative z-10 flex items-center justify-between px-4 h-11 flex-shrink-0"
          style={{ background: 'rgba(20,26,40,0.5)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
        >
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md flex items-center justify-center"
              style={{ background: 'rgba(102,148,255,0.35)', border: '1px solid rgba(102,148,255,0.2)' }}
            >
              <span className="text-[8px] font-black text-white">C2</span>
            </div>
            <span className="text-xs font-bold text-white/90">MDT</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-mono font-semibold text-cyan-300">
              {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
            </span>
            <button
              onClick={async () => {
                const resourceStr = localStorage.getItem('c2_mdt_resource');
                if (resourceStr) {
                  try {
                    const resource = JSON.parse(resourceStr) as Resource;
                    await fetch(`${API_BASE_URL}/resources/${resource.id}/release`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                    });
                  } catch { /* ignore */ }
                }
                localStorage.removeItem('c2_mdt_resource');
                localStorage.removeItem('c2_mdt_event');
                dispatch(logout());
                navigate('/login');
              }}
              className="w-6 h-6 rounded-md flex items-center justify-center transition-colors"
              style={{ background: 'rgba(255,80,116,0.12)', border: '1px solid rgba(255,80,116,0.2)' }}
              title="Logout"
            >
              <LogOut className="w-3 h-3 text-red-400" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="relative z-10 flex-1 overflow-auto min-h-0">{children}</main>

        {/* MDT Dispatch Alert Overlay */}
        <MdtDispatchAlert />

        {/* Glass Bottom Nav */}
        <nav className="relative z-10 flex items-center py-1.5 pb-3 flex-shrink-0"
          style={{ background: 'rgba(20,26,40,0.6)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', borderTop: '1px solid rgba(255,255,255,0.04)' }}
        >
          {tabs.map((tab) => {
            const isActive =
              tab.id === 'chat'
                ? chatOpen
                : tab.path === '/'
                ? location.pathname === '/' && !chatOpen
                : location.pathname.startsWith(tab.path) && !chatOpen;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab)}
                className={cn(
                  'flex-1 flex flex-col items-center gap-1 py-1 transition-colors',
                  isActive ? 'text-blue-400' : 'text-white/40'
                )}
              >
                <tab.icon className="w-[19px] h-[19px]" />
                <span className="text-[8px] font-semibold">{tab.label}</span>
                {isActive && <div className="w-1 h-1 rounded-full bg-blue-400" />}
              </button>
            );
          })}
        </nav>
      </div>
    </MobileContext.Provider>
  );
}
